import { useEffect, useId } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import { observer } from "mobx-react-lite";
import type { MapState } from "src/types/MapState";
import { buildWmsTileUrl } from "src/types/stencil/wmsCapabilities";
import { THREE_LAYER_ID } from "./Map/Layers/three-layer/Three.layer";

export const StencilMapLayers = observer(function StencilMapLayers({
  mapState,
}: {
  mapState: MapState;
}) {
  const { stencil } = mapState;
  const sourceId = useId();
  const mapRef = useMap();

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const onError = (e: { sourceId?: string }) => {
      if (e.sourceId === sourceId) stencil.setError("stencil.error.tileLoad");
    };
    map.on("error", onError);
    return () => {
      map.off("error", onError);
    };
  }, [sourceId, mapRef, stencil]);

  // Keep the stencil raster below the 3D buildings and below the three.js
  // design layer, so opacity only blends the overlay vs the basemap. Without
  // this, the raster mounts at the top of the stack and dims everything it
  // covers (including the generated design).
  useEffect(() => {
    if (stencil.mode.kind !== "wms" && stencil.mode.kind !== "image-locked") {
      return;
    }
    const map = mapRef.current?.getMap();
    if (!map) return;

    const moveBelowOccluders = () => {
      if (!map.getLayer(sourceId)) return;
      const layers = map.getStyle()?.layers ?? [];
      // Pick the lowest-indexed occluder; placing the stencil before it puts it
      // below all 3D buildings + the three.js design layer.
      const occluder = layers.find(
        (l) =>
          l.id !== sourceId && (l.type === "custom" || l.type === "fill-extrusion")
      );
      if (!occluder) return;
      try {
        map.moveLayer(sourceId, occluder.id);
      } catch {
        /* layer disposal race — ignore */
      }
    };

    moveBelowOccluders();
    // Try again on the next tick — react-map-gl adds the Layer asynchronously
    // after this effect runs, so the first call often finds no stencil layer.
    const raf = requestAnimationFrame(moveBelowOccluders);
    // Re-stack whenever the style changes (style switch, layer add/remove,
    // three.js custom layer mounting after the stencil).
    map.on("styledata", moveBelowOccluders);
    return () => {
      cancelAnimationFrame(raf);
      map.off("styledata", moveBelowOccluders);
    };
  }, [sourceId, mapRef, stencil.mode.kind]);

  if (stencil.mode.kind === "image-locked") {
    return (
      <Source
        key={`${sourceId}-image`}
        id={sourceId}
        type="image"
        url={stencil.mode.imageUrl}
        coordinates={stencil.mode.coords}
      >
        <Layer
          id={sourceId}
          beforeId={THREE_LAYER_ID}
          type="raster"
          paint={{ "raster-opacity": stencil.cssOpacity, "raster-fade-duration": 0 }}
        />
      </Source>
    );
  }
  if (stencil.mode.kind === "wms") {
    const url = buildWmsTileUrl(stencil.mode.capabilities, stencil.mode.selectedLayerName);
    return (
      <Source key={`${sourceId}-wms`} id={sourceId} type="raster" tiles={[url]} tileSize={256}>
        <Layer
          id={sourceId}
          beforeId={THREE_LAYER_ID}
          type="raster"
          paint={{ "raster-opacity": stencil.cssOpacity }}
        />
      </Source>
    );
  }
  return null;
});
